import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Subscription, lastValueFrom } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { DeviceGroup } from 'src/app/models/data/device';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Mode } from 'src/app/models/model';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { MatDialog } from '@angular/material/dialog';
import { RenameDeviceGroupDialogComponent } from '../../rename-device-group-dialog/rename-device-group-dialog.component';
import {
	allowWrites,
	isEmbedded,
	ownershipCompanyId as resolveOwnershipCompanyId,
} from '../company-detail-embed';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-device-groups',
	templateUrl: './device-groups.component.html',
	styleUrls: ['./device-groups.component.scss', '../../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatIcon,
		DataSetTableMenuComponent,
		HasSystemCapabilityDirective,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DataSetLoadingRowComponent,
	],
})
export class DeviceGroupsComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	/** Route company when embedded on company detail (ITE-12004). */
	@Input() companyId?: string;
	/** When embedded, parent controls write chrome (Oracle vs Support/Billing). */
	@Input() canWrite?: boolean;

	displayedColumns: string[] = ['name', 'deviceCount', 'source', 'rowMenu'];
	dataSource = new MatTableDataSource<{
		group: DeviceGroup;
		deviceCount?: number;
		controlAssignmentCount?: number;
	}>();

	filterLabels = {
		defaultPlaceholder: $localize`:@@deviceGroups-defaultPlaceholder:Search group name...`,
		groupName: $localize`:@@deviceGroups-filterLabelGroupName:Group Name`,
	};

	savedFilters?: QueryParam[];

	private filterSubscription?: Subscription;

	companyIsSentrilock = false;
	canEditDevices: boolean;

	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	get isEmbeddedInCompanyDetail(): boolean {
		return isEmbedded(this.companyId);
	}

	/** Write chrome: DM on left-nav; parent canWrite when embedded. */
	get allowDeviceWrites(): boolean {
		return allowWrites(this.companyId, this.canWrite, this.canEditDevices);
	}

	isOwnedDeviceGroup(deviceGroup: DeviceGroup): boolean {
		const companyId = resolveOwnershipCompanyId(
			this.companyId,
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId
		);
		return deviceGroup.companyId === companyId;
	}

	canShowDeviceGroupRowMenu(row: { group: DeviceGroup }): boolean {
		if (this.isEmbeddedInCompanyDetail) {
			return this.allowDeviceWrites && this.isOwnedDeviceGroup(row.group);
		}
		return this.isOwnedDeviceGroup(row.group) || this.companyIsSentrilock;
	}

	get getDeviceGroupsLoadingKey(): string {
		return this.companyId
			? ApiLoadingKeys.getDeviceGroupsByCompanyId
			: ApiLoadingKeys.getDeviceGroups;
	}
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly userSessionService: UserSessionService,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		private dialog: MatDialog
	) {
		this.canEditDevices = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
	}

	async ngAfterViewInit(): Promise<void> {
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.companyIsSentrilock =
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId === sentrilockId;

		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					filter.push({ param: 'isSystemGenerated', value: false });
					const result = await this.fetchDeviceGroups(filter);
					this.finishedLoadingInitData = true;
					this.savedFilters = filter;
					return result;
				})
			) // todo - filters
			.subscribe((data) => {
				this.dataSource.data = data;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	private fetchDeviceGroups(filter?: QueryParam[]) {
		if (this.companyId) {
			return this.apiService.getDeviceGroupsByCompanyId(this.companyId, filter);
		}
		return this.apiService.getDeviceGroups(filter);
	}

	navigateToAddDeviceGroup(): void {
		// Embedded: show Add for Oracle but do not open the active-company wizard.
		if (this.isEmbeddedInCompanyDetail) {
			return;
		}
		this.router.navigateByUrl('/add-device-group');
	}

	onRowClick(row: any): void {
		if (this.companyId) {
			this.router.navigate([
				`/company/${this.companyId}/detail/device-group/${row.group.id}`,
			]);
			return;
		}
		const mode: Mode = this.isOwnedDeviceGroup(row.group)
			? Mode.edit
			: Mode.viewOnly;
		this.router.navigate(['/device-group', row.group.id], { state: { mode } });
	}

	async renameGroupMenuOptionClicked(row: any): Promise<void> {
		if (!row) {
			return;
		}
		await lastValueFrom(
			this.dialog
				.open(RenameDeviceGroupDialogComponent, {
					panelClass: 'rename-device-group-setting-dialog',
					data: {
						deviceGroupData: row,
					},
				})
				.afterClosed()
		).then(async (dataSaved: any) => {
			if (dataSaved) {
				const result = await this.fetchDeviceGroups(this.savedFilters);
				this.dataSource.data = result;
			}
		});
	}

	rowId(row: {
		group: DeviceGroup;
		deviceCount?: number;
		controlAssignmentCount?: number;
	}): string {
		return row?.group?.id || 'unknown-id';
	}
}
